import { useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Input";
import { Pill } from "@/components/ui/Pill";
import { ApiError, post } from "@/lib/api";
import { S } from "@/lib/strings";
import type { TaskDraft, TaskSpec } from "@/types/fleet";

/**
 * Describe the job in a sentence; the model that drives the phone writes the spec.
 *
 * Filling this form by hand means knowing what `visionMode` and `thinkingPolicy` do, that
 * `allowedPackages` takes Android package names, that `extractionSchema` is a JSON Schema, and
 * that the goal has to spell out which filter to tick because the agent reads it literally. That
 * is a form for whoever built the platform.
 *
 * The draft is applied to the form, not saved. Nothing reaches a phone until the operator has
 * read it and pressed 儲存, and an imperfect draft is still a far better starting point than an
 * empty textarea.
 */
export function TaskAssistant({
  current,
  taskId,
  onApply,
}: {
  current: TaskSpec;
  taskId: string | null;
  onApply: (draft: TaskDraft) => void;
}) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState<string | null>(null);
  const [issues, setIssues] = useState<TaskDraft["errors"]>([]);
  const [unknownApps, setUnknownApps] = useState<string[]>([]);

  const hasExisting = !!current.goal.trim();

  async function generate() {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    setNotes(null);
    setIssues([]);
    setUnknownApps([]);
    try {
      const draft = await post<TaskDraft>("/tasks/draft", {
        prompt: prompt.trim(),
        current: hasExisting ? current : null,
        task_id: taskId,
      });
      onApply(draft);
      setNotes(draft.notes || null);
      setIssues(draft.errors ?? []);
      setUnknownApps(draft.unknown_packages ?? []);
      toast.success(S.assistantApplied);
    } catch (e) {
      // 503 means the endpoint or key is simply not set, which is a place to go and fix rather
      // than a failure to report.
      const unavailable = e instanceof ApiError && e.status === 503;
      toast.error(unavailable ? S.assistantUnavailable : S.mutationFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-[rgba(94,106,210,0.35)] bg-[rgba(94,106,210,0.06)] p-3.5">
      <div className="mb-1.5 flex items-center gap-1.5 text-[13px] font-medium">
        <Sparkles size={13} className="text-[var(--color-accent)]" />
        {S.assistantTitle}
      </div>
      <p className="mb-2.5 text-[12px] leading-relaxed text-[var(--color-fg-subtle)]">
        {S.assistantHint}
      </p>

      <Textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        className="font-sans text-[13px]"
        placeholder={S.assistantPlaceholder}
        onKeyDown={(e) => {
          // Enter alone is a newline: this is prose, and people write more than one line in it.
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void generate();
        }}
      />

      <div className="mt-2 flex items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          onClick={() => void generate()}
          disabled={busy || !prompt.trim()}
        >
          <Sparkles size={12} />
          {busy ? S.assistantWorking : hasExisting ? S.assistantImprove : S.assistantGenerate}
        </Button>
      </div>

      {notes && (
        <div className="mt-2.5 border-t border-[rgba(94,106,210,0.25)] pt-2.5">
          <div className="text-[11px] font-medium uppercase tracking-wider text-[var(--color-fg-subtle)]">
            {S.assistantNotes}
          </div>
          <p className="mt-0.5 text-[12px] leading-relaxed text-[var(--color-fg-muted)]">{notes}</p>
        </div>
      )}

      {unknownApps.length > 0 && (
        <div className="mt-2.5 border-t border-[rgba(94,106,210,0.25)] pt-2.5 text-[12px] text-[#fbbf24]">
          {S.assistantUnknownApps}
          <div className="mt-0.5 font-mono text-[11px]">{unknownApps.join(", ")}</div>
        </div>
      )}

      {issues.length > 0 && (
        <div className="mt-2.5 border-t border-[rgba(94,106,210,0.25)] pt-2.5">
          <div className="mb-1 flex items-center gap-1.5">
            <Pill variant="warn" size="sm">
              {S.assistantIssues}
            </Pill>
          </div>
          <ul className="space-y-0.5 text-[12px] text-[#fbbf24]">
            {issues.map((e, i) => (
              <li key={i}>
                <span className="font-mono text-[11px]">{e.path}</span> — {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
